import { useEffect, useMemo, useRef } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  applyOverride,
  createRun,
  forcePlaceOverlaps,
  getReassignOptions,
  getRun,
  listRuns,
} from "./runs-api";
import { ISSUES_KEY } from "./use-issues";
import { SCHEDULES_KEY, SCHEDULE_KEY } from "./use-schedules";
import type {
  ApplyOverrideRequest,
  ForcePlaceRequest,
  ForcePlaceResult,
  ReassignOption,
  Run,
  RunStatus,
  RunSummary,
} from "../types";

/**
 * How often to re-poll the runs list while a run is executing on the backend
 * (the solve runs on a background worker; we poll to pick up status + true
 * progress). Stops automatically once no run is in a non-terminal status.
 */
const RUN_POLL_INTERVAL_MS = 2000;

/** A run the backend is still working on (queued or actively solving). */
export function isRunActive(status: RunStatus): boolean {
  return status === "Running" || status === "Pending";
}

/**
 * TanStack Query cache key for the runs list of a single schedule.
 * Mirrors `ISSUES_KEY` / `ROOMS_KEY` shape: namespace tuple keyed on
 * scheduleId so invalidations on one schedule don't sweep others.
 */
export const RUNS_KEY = (scheduleId: string) =>
  ["schedule-runs", scheduleId] as const;

/**
 * Cache key for a single run. Read by detail screens (Phase 5+) and any
 * future run-comparison view.
 */
export const RUN_KEY = (scheduleId: string, runId: string) =>
  ["schedule-run", scheduleId, runId] as const;

/**
 * Cache key for the sections list of a single schedule. Owned by the
 * sections feature (Slice 6) but declared here so {@link useCreateRun} can
 * invalidate it on success without dragging in a circular import.
 */
export const SECTIONS_KEY = (scheduleId: string) =>
  ["schedule-sections", scheduleId] as const;

/**
 * Cache key for the reassign-options query — scoped to a particular
 * `(scheduleId, runId, studentIdentifier, courseId)` tuple. Stale time on the
 * underlying query is short (1 minute) because the options depend on the
 * latest run snapshot, which can change as overrides land.
 */
export const REASSIGN_OPTIONS_KEY = (
  scheduleId: string,
  runId: string,
  studentIdentifier: string,
  courseId: string,
) =>
  [
    "schedule-reassign-options",
    scheduleId,
    runId,
    studentIdentifier,
    courseId,
  ] as const;

export function useRuns(scheduleId: string) {
  return useQuery({
    queryKey: RUNS_KEY(scheduleId),
    queryFn: () => listRuns(scheduleId),
    enabled: !!scheduleId,
    // While any run is executing, poll so the UI picks up status + live
    // progress — and, critically, so a page refresh mid-solve reconnects to the
    // in-progress run and keeps updating until it finishes. Idle otherwise.
    refetchInterval: (query) => {
      const runs = query.state.data;
      const active = runs?.some((r) => isRunActive(r.status)) ?? false;
      return active ? RUN_POLL_INTERVAL_MS : false;
    },
  });
}

export function useRun(scheduleId: string, runId: string) {
  return useQuery({
    queryKey: RUN_KEY(scheduleId, runId),
    queryFn: () => getRun(scheduleId, runId),
    enabled: !!scheduleId && !!runId,
  });
}

/**
 * Derives the most-recent run from {@link useRuns}. Pure client-side projection
 * (sort by `startedAt` descending, take the first) — no extra fetch. Returns
 * `undefined` while the runs list is loading or when the schedule has no
 * runs yet.
 *
 * Backend `listRuns` already orders by `StartedAt DESC`, but we re-sort
 * defensively so a stray cache mutation can't surface an older run as "latest".
 */
export function useLatestRun(
  scheduleId: string,
): Pick<
  UseQueryResult<RunSummary | undefined, Error>,
  "data" | "isLoading" | "isError"
> {
  const runsQuery = useRuns(scheduleId);
  const latest = useMemo<RunSummary | undefined>(() => {
    const runs = runsQuery.data;
    if (!runs || runs.length === 0) return undefined;
    // Compare via the underlying epoch ms — strings are ISO 8601 and sort
    // lexically too, but Date comparison guards against malformed inputs.
    const sorted = [...runs].sort(
      (a, b) =>
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
    );
    return sorted[0];
  }, [runsQuery.data]);
  return {
    data: latest,
    isLoading: runsQuery.isLoading,
    isError: runsQuery.isError,
  };
}

export interface UseCreateRunOptions {
  /** Called after the mutation succeeds AND caches have been invalidated. */
  onSuccess?: (run: Run) => void;
  /** Called on any error path. Receives the raw thrown error (typically an AxiosError). */
  onError?: (error: unknown) => void;
}

/**
 * Mutation hook for POST `/runs`. The run now executes on a background worker,
 * so a successful POST only means the run was **queued** (status `Running`) —
 * not that assignments/issues exist yet. We therefore invalidate ONLY
 * {@link RUNS_KEY} here so the new Running row appears and {@link useRuns}
 * starts polling. The downstream caches (issues, sections, schedule counts) are
 * invalidated when the run actually *completes* — see {@link useRunCompletionSync}.
 *
 * Errors do NOT invalidate any caches (auth errors, etc. leave cached data
 * untouched).
 */
export function useCreateRun(
  scheduleId: string,
  options?: UseCreateRunOptions,
) {
  const qc = useQueryClient();
  // The mutate variable is the optional per-run solver time limit in seconds
  // (undefined → configured default). `void` keeps bare `mutate()` calls working.
  return useMutation<Run, unknown, number | void>({
    mutationFn: (timeLimitSeconds) =>
      createRun(scheduleId, timeLimitSeconds ?? undefined),
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: RUNS_KEY(scheduleId) });
      options?.onSuccess?.(run);
    },
    onError: (err) => {
      options?.onError?.(err);
    },
  });
}

/**
 * Watches the latest run's status and, when it transitions from an active
 * (Running/Pending) state to a terminal one, invalidates the caches a completed
 * run affects — mirroring the old synchronous {@link useCreateRun} invalidation
 * set (keep in sync with `docs/solutions/integration-issues/
 * cross-cache-invalidation-include-parent-list.md`):
 *
 *   1. {@link ISSUES_KEY} — Stage 3 PostRun issues were (re)written.
 *   2. {@link SCHEDULE_KEY} — `issueCounts` changed (counts include PostRun).
 *   3. {@link SCHEDULES_KEY} — the parent list shows per-schedule issue counts.
 *   4. {@link SECTIONS_KEY} — section `assignedCount` reflects the latest run.
 *
 * Mount once per schedule detail view. Fires on the poll tick that first sees
 * the run terminal, so the page updates whether the run was started in this tab
 * or picked up from a refresh mid-solve.
 */
export function useRunCompletionSync(scheduleId: string): void {
  const qc = useQueryClient();
  const { data: latest } = useLatestRun(scheduleId);
  const prevStatusRef = useRef<RunStatus | undefined>(undefined);

  const status = latest?.status;
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;
    if (!status) return;
    const wasActive = prev !== undefined && isRunActive(prev);
    const nowTerminal = status === "Succeeded" || status === "Failed";
    if (wasActive && nowTerminal) {
      qc.invalidateQueries({ queryKey: ISSUES_KEY(scheduleId) });
      qc.invalidateQueries({ queryKey: SCHEDULE_KEY(scheduleId) });
      qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
      qc.invalidateQueries({ queryKey: SECTIONS_KEY(scheduleId) });
    }
  }, [status, scheduleId, qc]);
}

/**
 * Query hook for the reassign-options endpoint. Disabled when any of the four
 * input scalars is falsy so the modal can mount before the user has actually
 * picked a (student, course) pair (or before a run id is known) without
 * spurious fetches.
 *
 * Stale time is short (60 seconds) because override mutations replace the
 * latest run's assignment list — letting the query auto-refetch on next focus
 * keeps the modal honest if the user has been idle.
 */
export function useReassignOptions(
  scheduleId: string,
  runId: string | undefined,
  studentIdentifier: string | undefined,
  courseId: string | undefined,
) {
  const enabled = !!scheduleId && !!runId && !!studentIdentifier && !!courseId;
  return useQuery<ReassignOption[]>({
    queryKey: REASSIGN_OPTIONS_KEY(
      scheduleId,
      runId ?? "",
      studentIdentifier ?? "",
      courseId ?? "",
    ),
    queryFn: () => {
      // The `enabled` gate below keeps this query off the wire whenever any
      // scalar is falsy. Throwing here documents that contract for any caller
      // (or future TanStack-internal path) that might try to call the
      // queryFn directly — and dodges the `as string` cast the previous
      // implementation needed to compile.
      if (!runId || !studentIdentifier || !courseId) {
        throw new Error(
          "useReassignOptions called with falsy scalars — enabled gate violated",
        );
      }
      return getReassignOptions(scheduleId, runId, studentIdentifier, courseId);
    },
    enabled,
    staleTime: 60 * 1000,
  });
}

export interface UseApplyOverrideOptions {
  /** Called after the mutation succeeds AND caches have been invalidated. */
  onSuccess?: (run: Run) => void;
  /** Called on any error path. Receives the raw thrown error. */
  onError?: (error: unknown) => void;
}

/**
 * Mutation hook for POST `/runs/{runId}/assignments` (manual reassignment).
 * On success, invalidates **six** caches — the Phase 4 five-key invalidation
 * set extended with `RUN_KEY` (the run that was overridden's full payload).
 * Errors leave caches untouched.
 *
 *   1. {@link RUNS_KEY} — run list (assignment counts updated).
 *   2. {@link RUN_KEY} — the run that was overridden (assignments changed).
 *   3. {@link ISSUES_KEY} — PostRun issues were replaced.
 *   4. {@link SCHEDULE_KEY} — `issueCounts` changed.
 *   5. {@link SCHEDULES_KEY} — list page surfaces per-schedule issue counts.
 *   6. {@link SECTIONS_KEY} — per-section assigned counts changed.
 *
 * See `docs/solutions/integration-issues/cross-cache-invalidation-five-keys-sections.md`
 * for the general "list every cache this mutation affects" rule.
 */
export function useApplyOverride(
  scheduleId: string,
  options?: UseApplyOverrideOptions,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      request,
    }: {
      runId: string;
      request: ApplyOverrideRequest;
    }) => applyOverride(scheduleId, runId, request),
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: RUNS_KEY(scheduleId) });
      qc.invalidateQueries({ queryKey: RUN_KEY(scheduleId, run.id) });
      qc.invalidateQueries({ queryKey: ISSUES_KEY(scheduleId) });
      qc.invalidateQueries({ queryKey: SCHEDULE_KEY(scheduleId) });
      qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
      qc.invalidateQueries({ queryKey: SECTIONS_KEY(scheduleId) });
      options?.onSuccess?.(run);
    },
    onError: (err) => {
      options?.onError?.(err);
    },
  });
}

export interface UseForcePlaceOverlapsOptions {
  /** Called after the mutation succeeds (preview or apply). */
  onSuccess?: (result: ForcePlaceResult) => void;
  /** Called on any error path. Receives the raw thrown error. */
  onError?: (error: unknown) => void;
}

/**
 * Mutation hook for POST `/runs/{runId}/force-place-overlaps` (accept small
 * overlaps). A dry-run preview writes nothing, so caches are only invalidated
 * when the result was actually applied — the same six-key set as
 * {@link useApplyOverride}.
 */
export function useForcePlaceOverlaps(
  scheduleId: string,
  options?: UseForcePlaceOverlapsOptions,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      request,
    }: {
      runId: string;
      request: ForcePlaceRequest;
    }) => forcePlaceOverlaps(scheduleId, runId, request),
    onSuccess: (result) => {
      if (result.applied) {
        qc.invalidateQueries({ queryKey: RUNS_KEY(scheduleId) });
        qc.invalidateQueries({ queryKey: RUN_KEY(scheduleId, result.runId) });
        qc.invalidateQueries({ queryKey: ISSUES_KEY(scheduleId) });
        qc.invalidateQueries({ queryKey: SCHEDULE_KEY(scheduleId) });
        qc.invalidateQueries({ queryKey: SCHEDULES_KEY });
        qc.invalidateQueries({ queryKey: SECTIONS_KEY(scheduleId) });
      }
      options?.onSuccess?.(result);
    },
    onError: (err) => {
      options?.onError?.(err);
    },
  });
}
